import * as React from "react";
import { useCallback } from "react";
import { Button, Gapped, Loader, Modal } from "react-ui";

import {
    EXPERT_PREVIEW_PAGE_ATTACH_CLICK,
    EXPERT_PREVIEW_PAGE_CANCEL_CLICK,
    EXPERT_PREVIEW_PAGE_CANCEL_REQUEST,
    EXPERT_PREVIEW_PAGE_DOWNLOAD_CLICK,
    EXPERT_PREVIEW_PAGE_RELOAD_NOTE_CLICK,
} from "../../metricsAction";
import {
    CreateExplanatoryNoteResponse,
    CreateExplanatoryNoteStatus,
} from "../../../../../Common/CandyApiClient/Types/ExplanatoryNote/CreateExplanatoryNoteResponse";
import { Icon } from "../../Icons/Icon";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";

import { useNotePreviewUrl } from "./useNotePreviewUrl";
import {
    Footer,
    LoadingNote,
    Message,
    MessageBoxErrorLog,
    PreviewNoteError,
    ViewNoteObjectTag,
} from "./PreviewNote.styles";

export interface Props {
    explanatoryNote: CreateExplanatoryNoteResponse | undefined;
    error: boolean;
    hideAttachButton?: boolean;
    isReadOnly?: boolean;
    handleClose: () => void;
    handleAttachNote: () => void;
    handleDownloadNote: () => void;
    handleReloadNote: () => void;
}

export function PreviewNote(props: Props) {
    const track = useMetrics();
    const isNoteCreated = checkIsExplanatoryNoteCreated(props.explanatoryNote);

    const handleClose = useCallback(() => {
        props.handleClose();
        track(EXPERT_PREVIEW_PAGE_CANCEL_REQUEST);
    }, [props.handleClose]);

    const handleCancel = useCallback(() => {
        props.handleClose();
        track(EXPERT_PREVIEW_PAGE_CANCEL_CLICK);
    }, [props.handleClose]);

    const handleDownload = useCallback(() => {
        props.handleDownloadNote();
        track(EXPERT_PREVIEW_PAGE_DOWNLOAD_CLICK);
    }, [props.handleDownloadNote]);

    const renderActionButton = () => {
        if (!props.hideAttachButton && !props.isReadOnly) {
            if (isNoteCreated) {
                return <AttachToReportButton onAttach={props.handleAttachNote} />;
            }

            if (!props.error && !props.explanatoryNote) {
                return <AttachToReportButton disabled onAttach={props.handleAttachNote} />;
            }
        }

        if (props.error) {
            return <NoteReloadButton onReload={props.handleReloadNote} />;
        }

        return <></>;
    };

    return (
        <Modal onClose={handleClose}>
            <Modal.Header>{renderHeaderCaption(props)}</Modal.Header>

            <Modal.Body>
                <PreviewBody explanatoryNote={props.explanatoryNote} error={props.error} onDownload={handleDownload} />
            </Modal.Body>

            <Modal.Footer panel>
                <PreviewFooter
                    isNoteCreated={isNoteCreated}
                    actionButton={renderActionButton()}
                    onCancel={handleCancel}
                    onDownload={handleDownload}
                />
            </Modal.Footer>
        </Modal>
    );
}

function renderHeaderCaption({ explanatoryNote, error }: Pick<Props, "explanatoryNote" | "error">) {
    const isNoteCreated = checkIsExplanatoryNoteCreated(explanatoryNote);

    if (error) {
        return "Финансовый анализ не сформирован";
    }

    if (explanatoryNote) {
        if (isNoteCreated) {
            return "Финансовый анализ";
        } else {
            return "Финансовый анализ не сформирован";
        }
    }

    return "Финансовый анализ формируется";
}

interface PreviewBodyProps {
    explanatoryNote: CreateExplanatoryNoteResponse | undefined;
    error: boolean;
    onDownload: () => void;
}

function PreviewBody({ explanatoryNote, error, onDownload }: PreviewBodyProps): JSX.Element {
    const isNoteCreated = checkIsExplanatoryNoteCreated(explanatoryNote);

    const errorMessage =
        explanatoryNote?.status === CreateExplanatoryNoteStatus.Error ? explanatoryNote.errorMessage : undefined;

    if (!explanatoryNote) {
        return <BeforeNoteCreatedContent error={error} />;
    }

    if (isNoteCreated) {
        return <NotePreview explanatoryNote={explanatoryNote} onDownload={onDownload} />;
    }

    return <NoteCreationError message={errorMessage} />;
}

interface PreviewFooterProps {
    isNoteCreated: boolean;
    actionButton: React.ReactElement;
    onCancel: () => void;
    onDownload: () => void;
}

function PreviewFooter({ isNoteCreated, actionButton, onCancel, onDownload }: PreviewFooterProps): JSX.Element {
    return (
        <Footer>
            <Gapped gap={10}>
                {actionButton}
                <Button onClick={onCancel} size="medium" data-tid="CancelButtonFormed">
                    Отменить
                </Button>
            </Gapped>
            {isNoteCreated && (
                <Button use="link" onClick={onDownload}>
                    <Icon name="NetDownloadIcon" />
                    Скачать
                </Button>
            )}
        </Footer>
    );
}

function BeforeNoteCreatedContent(props: { error: boolean }): JSX.Element {
    return props.error ? <NoteCreationError /> : <Preloader />;
}

function NoteCreationError(props: { message?: string }): JSX.Element {
    return (
        <PreviewNoteError>
            <Message>Модификатор «Эксперт» не сформировал финансовый анализ, попробуйте снова.</Message>
            {props.message && <MessageBoxErrorLog>{props.message}</MessageBoxErrorLog>}
        </PreviewNoteError>
    );
}

function Preloader(): JSX.Element {
    return (
        <LoadingNote>
            <Loader active type="big" caption="Формируется..." />
        </LoadingNote>
    );
}

interface NotePreviewProps {
    explanatoryNote: CreateExplanatoryNoteResponse;
    onDownload: () => void;
}

function NotePreview({ explanatoryNote, onDownload }: NotePreviewProps): JSX.Element {
    const notePreviewUrl = useNotePreviewUrl(explanatoryNote);
    const fallback = <PreviewError onDownload={onDownload} />;

    if (notePreviewUrl) {
        return <ViewNote notePreviewUrl={notePreviewUrl} fallback={fallback} />;
    }

    return fallback;
}

function ViewNote(props: { notePreviewUrl: string; fallback: JSX.Element }) {
    return (
        <ViewNoteObjectTag data={props.notePreviewUrl} type="application/pdf">
            {props.fallback}
        </ViewNoteObjectTag>
    );
}

interface PreviewErrorProps {
    onDownload: () => void;
}

function PreviewError({ onDownload }: PreviewErrorProps) {
    return (
        <PreviewNoteError>
            <Message>
                Не удалось отобразить превью финансового анализа. Вы можете его&nbsp;
                <Button use="link" onClick={onDownload} size={"medium"}>
                    скачать.
                </Button>
            </Message>
        </PreviewNoteError>
    );
}

function AttachToReportButton(props: { disabled?: boolean; onAttach: () => void }) {
    const track = useMetrics();

    const attachNote = useCallback(() => {
        props.onAttach();
        track(EXPERT_PREVIEW_PAGE_ATTACH_CLICK);
    }, [props.onAttach]);

    return (
        <Button disabled={props.disabled} onClick={attachNote} use="primary" size="medium" data-tid="AddToReport">
            Прикрепить к отчету
        </Button>
    );
}

function NoteReloadButton(props: { onReload: () => void }) {
    const track = useMetrics();

    const reloadNote = useCallback(() => {
        props.onReload();
        track(EXPERT_PREVIEW_PAGE_RELOAD_NOTE_CLICK);
    }, [props.onReload]);

    return (
        <Button onClick={reloadNote} use="primary" size="medium">
            Попробовать снова
        </Button>
    );
}

function checkIsExplanatoryNoteCreated(explanatoryNote: CreateExplanatoryNoteResponse | undefined) {
    return explanatoryNote?.status === CreateExplanatoryNoteStatus.Success;
}
